/**
 * 客户端诊断 — 连接状态 + 延迟探测 + 自测报告
 */

export interface DiagnosticResult {
  timestamp: number
  connection: { state: string; latencyMs: number; reconnects: number }
  system: { uptime: number; memoryUsage: number; platform: string }
  health: { score: number; issues: string[] }
}

export interface LatencyProbe {
  target: string
  latencyMs: number
  success: boolean
  timestamp: number
}

/**
 * 客户端诊断管理器
 */
export class ClientDiagnostics {
  private probeHistory: LatencyProbe[] = []
  private reconnectCount = 0
  private startTime = Date.now()
  private issues: string[] = []
  private maxHistory: number

  constructor(maxHistory = 100) {
    this.maxHistory = maxHistory
  }

  /** 记录延迟探测 */
  recordProbe(target: string, latencyMs: number, success: boolean): void {
    this.probeHistory.push({ target, latencyMs, success, timestamp: Date.now() })
    if (this.probeHistory.length > this.maxHistory) this.probeHistory.shift()
  }

  /** 记录重连 */
  recordReconnect(): void {
    this.reconnectCount++
  }

  /** 添加问题 */
  addIssue(issue: string): void {
    this.issues.push(issue)
    if (this.issues.length > 50) this.issues.shift()
  }

  /** 获取平均延迟 */
  getAverageLatency(): number {
    const successful = this.probeHistory.filter((p) => p.success)
    if (successful.length === 0) return -1
    return successful.reduce((s, p) => s + p.latencyMs, 0) / successful.length
  }

  /** 获取丢包率 */
  getPacketLoss(): number {
    if (this.probeHistory.length === 0) return 0
    const failed = this.probeHistory.filter((p) => !p.success).length
    return failed / this.probeHistory.length
  }

  /** 计算健康分数 (0-100) */
  calculateHealthScore(): number {
    let score = 100
    const avgLatency = this.getAverageLatency()
    if (avgLatency > 500) score -= 30
    else if (avgLatency > 200) score -= 15
    else if (avgLatency > 100) score -= 5

    const packetLoss = this.getPacketLoss()
    if (packetLoss > 0.1) score -= 40
    else if (packetLoss > 0.05) score -= 20
    else if (packetLoss > 0.01) score -= 10

    if (this.reconnectCount > 10) score -= 20
    else if (this.reconnectCount > 5) score -= 10

    return Math.max(0, score)
  }

  /** 生成诊断报告 */
  generateReport(connectionState: string): DiagnosticResult {
    return {
      timestamp: Date.now(),
      connection: {
        state: connectionState,
        latencyMs: this.getAverageLatency(),
        reconnects: this.reconnectCount,
      },
      system: {
        uptime: Date.now() - this.startTime,
        memoryUsage: process.memoryUsage?.().heapUsed || 0,
        platform: process.platform || 'unknown',
      },
      health: {
        score: this.calculateHealthScore(),
        issues: [...this.issues],
      },
    }
  }

  /** 获取探测历史 */
  getProbeHistory(): LatencyProbe[] {
    return [...this.probeHistory]
  }

  /** 重置 */
  reset(): void {
    this.probeHistory = []
    this.reconnectCount = 0
    this.issues = []
  }
}
